<script>
	import { onMount, onDestroy, tick } from 'svelte';
	import {
		fetchSourceItems,
		captureSourceBlock,
		getSourceBlock,
		deleteSourceBlock,
		getSourceDigestStatus,
		triggerSourceDigest,
	} from '../lib/api.js';
	import Navbar from '../components/Navbar.svelte';

	export let navigate;

	// ── Compose state ────────────────────────────────────────────────────────
	let composeInput = '';
	let composeNotes = '';
	let planting = false;
	let plantError = null;

	// ── Stream state ─────────────────────────────────────────────────────────
	let items = [];
	let streamLoading = false;
	let streamError = null;
	let expandedId = null;
	let expandedDetail = null;
	let expandedLoading = false;

	// ── Pagination state ─────────────────────────────────────────────────────
	let page = 1;
	let totalPages = 1;
	let totalItems = 0;
	const PAGE_SIZE = 100;

	// ── Lapidary state ───────────────────────────────────────────────────────
	let digest = null;
	let digestLoading = false;
	let submitting = false;
	let submitMsg = null;

	// ── Mobile tabs ──────────────────────────────────────────────────────────
	let mobileTab = 'source';   // 'compose' | 'lapidary' | 'source'
	let isMobile = false;

	// ── Polling ──────────────────────────────────────────────────────────────
	let pollHandle = null;
	let isFetchingBlocks = false;
	let isFetchingDigest = false;
	const POLL_MS = 15000;

	onMount(async () => {
		isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
		if (typeof window !== 'undefined') {
			window.addEventListener('resize', handleResize);
		}
		await Promise.all([refreshBlocks(), refreshDigest()]);
		pollHandle = setInterval(() => {
			refreshBlocks();
			refreshDigest();
		}, POLL_MS);
	});

	onDestroy(() => {
		if (pollHandle) clearInterval(pollHandle);
		if (typeof window !== 'undefined') {
			window.removeEventListener('resize', handleResize);
		}
	});

	function handleResize() {
		isMobile = window.innerWidth <= 768;
	}

	async function refreshBlocks() {
		// M1: guard against poll overlap — if a previous fetch is still in flight
		// (slow network or cold-start), skip this tick rather than racing.
		if (isFetchingBlocks) return;
		isFetchingBlocks = true;
		streamLoading = items.length === 0;
		streamError = null;
		try {
			const res = await fetchSourceItems({ page, limit: PAGE_SIZE });
			items = res.items || [];
			totalPages = res.total_pages || 1;
			totalItems = res.total || 0;
			// If a page was deleted and we're now past total_pages, clamp.
			if (page > totalPages) {
				page = totalPages;
			}
		} catch (err) {
			streamError = err.message;
		} finally {
			streamLoading = false;
			isFetchingBlocks = false;
		}
	}

	async function gotoPage(target) {
		if (target < 1 || target > totalPages || target === page) return;
		page = target;
		expandedId = null;
		expandedDetail = null;
		await refreshBlocks();
	}

	async function refreshDigest() {
		if (isFetchingDigest) return;
		isFetchingDigest = true;
		digestLoading = digest === null;
		try {
			digest = await getSourceDigestStatus();
		} catch { /* silent — non-critical */ } finally {
			digestLoading = false;
			isFetchingDigest = false;
		}
	}

	async function plant() {
		const content = composeInput.trim();
		if (!content || planting) return;
		planting = true;
		plantError = null;
		try {
			await captureSourceBlock({
				content,
				notes: composeNotes.trim() || undefined,
			});
			composeInput = '';
			composeNotes = '';
			await refreshBlocks();
			if (isMobile) {
				mobileTab = 'source';
			}
		} catch (err) {
			plantError = err.message;
		}
		planting = false;
	}

	function handleComposeKey(e) {
		if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
			e.preventDefault();
			plant();
		}
	}

	async function toggleExpand(block) {
		if (expandedId === block.id) {
			expandedId = null;
			expandedDetail = null;
			return;
		}
		// M2: capture target id and bail if user clicked a different row before
		// the fetch completed — prevents stale payload from briefly rendering
		// against the new expandedId.
		const targetId = block.id;
		expandedId = targetId;
		expandedDetail = null;
		expandedLoading = true;
		try {
			const detail = await getSourceBlock(targetId);
			if (expandedId !== targetId) return;
			// Backend returns { block, formed_concept? } — unwrap to surface the
			// fields our template expects (content / notes / captured_at / etc.)
			// while preserving the formed_concept enrichment.
			expandedDetail = detail?.block
				? { ...detail.block, formed_concept: detail.formed_concept || null }
				: detail;
		} catch (err) {
			if (expandedId !== targetId) return;
			expandedDetail = { error: err.message };
		} finally {
			if (expandedId === targetId) expandedLoading = false;
		}
	}

	async function pruneExpanded() {
		if (!expandedDetail?.id) return;
		const ok = confirm('Delete this source block? This cannot be undone.');
		if (!ok) return;
		try {
			await deleteSourceBlock(expandedDetail.id);
			expandedId = null;
			expandedDetail = null;
			await refreshBlocks();
		} catch (err) {
			expandedDetail = { ...expandedDetail, error: err.message };
		}
	}

	async function submitLapidary() {
		if (submitting) return;
		submitting = true;
		submitMsg = null;
		try {
			const res = await triggerSourceDigest();
			if (res.triggered) {
				submitMsg = `Run started · ${res.pending_block_count} blocks digesting`;
			} else if (res.skipped_reason === 'no_pending_blocks') {
				submitMsg = 'Nothing pending';
			} else if (res.skipped_reason === 'run_in_progress') {
				submitMsg = 'Run already in progress';
			} else {
				submitMsg = 'Submitted';
			}
			await refreshDigest();
		} catch (err) {
			submitMsg = err.message;
		}
		submitting = false;
		setTimeout(() => { submitMsg = null; }, 4000);
	}

	// Color classes by lifecycle stage. Documents always render as concepted.
	function rowClass(item) {
		if (item.type === 'document') return 'row-concepted';
		if (item.status === 'pending') return 'row-virgin';
		if (item.status === 'fast_indexed' || item.status === 'pending_append') return 'row-clustered';
		if (item.status === 'organized') return 'row-concepted';
		if (item.status === 'paused') return 'row-paused';
		return 'row-other';
	}

	// Friendly title. Blocks: first non-empty line of content. Documents: filename.
	function itemTitle(item) {
		if (item.type === 'document') {
			return item.identifier || '(unnamed document)';
		}
		const c = (item.content || '').trim();
		if (!c) return '(empty)';
		const firstLine = c.split('\n')[0].replace(/^#+\s*/, '').trim();
		return firstLine.length > 90 ? firstLine.slice(0, 90) + '…' : firstLine;
	}

	// Queued chip applies only to blocks that aren't yet 'organized'.
	function isQueued(item) {
		return item.type === 'block' && item.status !== 'organized';
	}

	function timeAgo(iso) {
		if (!iso) return '';
		const ms = Date.now() - new Date(iso).getTime();
		const s = Math.floor(ms / 1000);
		if (s < 60) return 'just now';
		const m = Math.floor(s / 60);
		if (m < 60) return `${m}m ago`;
		const h = Math.floor(m / 60);
		if (h < 24) return `${h}h ago`;
		const d = Math.floor(h / 24);
		return `${d}d ago`;
	}

	function formatCapturedAt(iso) {
		if (!iso) return '';
		const d = new Date(iso);
		const yyyy = d.getFullYear();
		const mm = String(d.getMonth() + 1).padStart(2, '0');
		const dd = String(d.getDate()).padStart(2, '0');
		let h = d.getHours();
		const ampm = h >= 12 ? 'PM' : 'AM';
		h = h % 12 || 12;
		return `${yyyy}/${mm}/${dd}-${h}${ampm}`;
	}

</script>

<svelte:head>
	<title>Source - ADRASI</title>
</svelte:head>

<div class="s-page">
	<Navbar active="source" {navigate} />

	{#if isMobile}
		<div class="s-tab-strip" role="tablist" aria-label="Source page sections">
			<button
				role="tab"
				aria-selected={mobileTab === 'compose'}
				aria-controls="s-compose-panel"
				class:active={mobileTab === 'compose'}
				on:click={() => mobileTab = 'compose'}
			>Compose</button>
			<button
				role="tab"
				aria-selected={mobileTab === 'lapidary'}
				aria-controls="s-lapidary-panel"
				class:active={mobileTab === 'lapidary'}
				on:click={() => mobileTab = 'lapidary'}
			>Lapidary</button>
			<button
				role="tab"
				aria-selected={mobileTab === 'source'}
				aria-controls="s-source-panel"
				class:active={mobileTab === 'source'}
				on:click={() => mobileTab = 'source'}
			>Source</button>
		</div>
	{/if}

	<div class="s-columns" class:mobile={isMobile}>
		<!-- Compose + Lapidary lane -->
		<div class="s-lane s-compose-lane" id="s-compose-panel" role={isMobile ? 'tabpanel' : undefined} class:hide={isMobile && mobileTab !== 'compose' && mobileTab !== 'lapidary'}>
			{#if !isMobile || mobileTab === 'compose'}
				<div class="s-lane-header" id="s-compose-label">Compose</div>
				<div class="s-compose-section">
					<label for="s-compose-textarea" class="s-sr-only">Source content</label>
					<label for="s-compose-notes" class="s-sr-only">Optional topic or note</label>
					<div class="s-compose-box">
						<textarea
							id="s-compose-textarea"
							class="s-compose-input"
							placeholder="Contribute information…"
							aria-labelledby="s-compose-label"
							bind:value={composeInput}
							on:keydown={handleComposeKey}
							disabled={planting}
							rows="6"
						></textarea>
						<input
							id="s-compose-notes"
							class="s-compose-notes"
							placeholder="(optional) topic/note"
							bind:value={composeNotes}
							disabled={planting}
						/>
					</div>
					<div class="s-compose-actions">
						<button class="s-plant" on:click={plant} disabled={!composeInput.trim() || planting}>
							{planting ? 'Adding…' : 'Add'}
						</button>
					</div>
					{#if plantError}
						<div class="s-error">{plantError}</div>
					{/if}
					<div class="s-protip">
						<p class="s-protip-body">
							Send any source material to <a href="mailto:submit@adrasi.com" class="s-protip-email">submit@adrasi.com</a> and it will be added to the queue.
						</p>
					</div>
				</div>
			{/if}

			{#if !isMobile || mobileTab === 'lapidary'}
				<div class="s-lane-header s-lapidary-header" id="s-lapidary-panel">Lapidary</div>
				<div class="s-lapidary-section">
					{#if digest}
						{#if digest.running}
							<div class="s-lap-row s-lap-running">
								<span class="s-lap-key">Status</span>
								<span class="s-lap-val">Running now</span>
							</div>
						{/if}
						<div class="s-lap-row">
							<span class="s-lap-key">Blocks in queue</span>
							<span class="s-lap-val">{digest.total_actionable ?? 0}</span>
						</div>
						{#if digest.last_digest_at}
							<div class="s-lap-row">
								<span class="s-lap-key">Last digest</span>
								<span class="s-lap-val">{timeAgo(digest.last_digest_at)}</span>
							</div>
						{/if}
						<div class="s-lap-row">
							<span class="s-lap-key">Cadence</span>
							<span class="s-lap-val">~hourly</span>
						</div>
						<button class="s-submit" on:click={submitLapidary} disabled={submitting || digest.running}>
							{submitting ? 'Submitting…' : digest.running ? 'Running…' : 'Submit now'}
						</button>
						{#if submitMsg}
							<div class="s-submit-msg">{submitMsg}</div>
						{/if}
					{:else if digestLoading}
						<div class="s-lap-empty">Loading…</div>
					{:else}
						<div class="s-lap-empty">Status unavailable</div>
					{/if}
				</div>
			{/if}
		</div>

		<!-- Stream lane -->
		<div class="s-lane s-stream-lane" id="s-source-panel" role={isMobile ? 'tabpanel' : undefined} class:hide={isMobile && mobileTab !== 'source'}>
			<div class="s-lane-header">
				Source
				<span class="s-lane-meta">{totalItems} {totalItems === 1 ? 'item' : 'items'}</span>
			</div>
			<div class="s-stream-scroll">
				{#if streamLoading}
					<div class="s-stream-empty">Loading…</div>
				{:else if streamError}
					<div class="s-stream-empty s-error">{streamError}</div>
				{:else if items.length === 0}
					<div class="s-stream-empty">
						<p>No source items yet.</p>
						<p class="s-empty-hint">Contribute information to begin.</p>
					</div>
				{:else}
					{#each items as block (block.id)}
						<div class="s-row {rowClass(block)}" class:expanded={expandedId === block.id}>
							<button class="s-row-button" on:click={() => toggleExpand(block)}>
								<div class="s-row-title">{itemTitle(block)}</div>
								<div class="s-row-meta">
									<span class="s-row-time">{formatCapturedAt(block.captured_at)} {timeAgo(block.captured_at)}</span>
									{#if block.origin}
										<span class="s-row-tag s-row-tag-origin">{block.origin}</span>
									{/if}
									{#if isQueued(block)}
										<span class="s-row-tag">queued</span>
									{/if}
								</div>
							</button>
							{#if expandedId === block.id}
								<div class="s-detail">
									{#if expandedLoading}
										<div class="s-detail-loading">Loading…</div>
									{:else if expandedDetail?.error}
										<div class="s-error">{expandedDetail.error}</div>
									{:else if expandedDetail}
										<div class="s-detail-body">{expandedDetail.content}</div>
										{#if expandedDetail.notes}
											<div class="s-detail-notes">
												<span class="s-detail-key">Notes</span>
												<span class="s-detail-val">{expandedDetail.notes}</span>
											</div>
										{/if}
										<div class="s-detail-grid">
											<div class="s-detail-cell">
												<span class="s-detail-key">Captured</span>
												<span class="s-detail-val">{new Date(expandedDetail.captured_at).toLocaleString()}</span>
											</div>
											<div class="s-detail-cell">
												<span class="s-detail-key">Status</span>
												<span class="s-detail-val">{expandedDetail.status}</span>
											</div>
											{#if expandedDetail.project}
												<div class="s-detail-cell">
													<span class="s-detail-key">Project</span>
													<span class="s-detail-val">{expandedDetail.project}</span>
												</div>
											{/if}
											{#if expandedDetail.attached_to_concept_id}
												<div class="s-detail-cell">
													<span class="s-detail-key">Concept</span>
													<a class="s-detail-link" href={`#/concept/${expandedDetail.attached_to_concept_id}`}>
														{expandedDetail.formed_concept?.concept || 'open ↗'}
													</a>
												</div>
											{/if}
										</div>
										<div class="s-detail-actions">
											<button class="s-detail-prune" on:click={pruneExpanded}>Delete</button>
										</div>
									{/if}
								</div>
							{/if}
						</div>
					{/each}
				{/if}
			</div>
			<div class="s-lane-footer">
				<div class="s-footer-group s-footer-left">
					<button class="s-footer-btn" type="button" disabled>Edit</button>
					<button class="s-footer-btn s-footer-btn-danger" type="button" disabled>Delete</button>
				</div>
				<div class="s-footer-group s-footer-right">
					<button class="s-footer-btn" type="button" on:click={() => gotoPage(page - 1)} disabled={page <= 1}>← Newer</button>
					<span class="s-footer-page">{page}/{totalPages}</span>
					<button class="s-footer-btn" type="button" on:click={() => gotoPage(page + 1)} disabled={page >= totalPages}>Older →</button>
				</div>
			</div>
		</div>
	</div>
</div>

<style>
	.s-sr-only {
		position: absolute;
		width: 1px;
		height: 1px;
		padding: 0;
		margin: -1px;
		overflow: hidden;
		clip: rect(0, 0, 0, 0);
		white-space: nowrap;
		border: 0;
	}

	.s-page {
		height: 100vh;
		display: flex;
		flex-direction: column;
		overflow: hidden;
		background: #E8EAEE;
		color: #1A1A1A;
		font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', sans-serif;
	}

	.s-tab-strip {
		display: none;
		background: #F1F3F7;
		border-bottom: 1px solid #D8DBE2;
	}
	.s-tab-strip button {
		flex: 1;
		padding: 12px 0;
		background: none;
		border: none;
		font-size: 13px;
		font-weight: 500;
		color: #6B7280;
		cursor: pointer;
	}
	.s-tab-strip button.active {
		color: #1F3A68;
		border-bottom: 2px solid #1F3A68;
	}

	.s-columns {
		display: grid;
		grid-template-columns: 1fr 2fr;
		gap: 0;
		background: #F1F3F7;
		flex: 1;
		min-height: 0;
		overflow: hidden;
		border-top: 1px solid #D8DBE2;
	}
	.s-columns.mobile {
		grid-template-columns: 1fr;
	}

	.s-lane {
		display: flex;
		flex-direction: column;
		min-height: 0;
		overflow: hidden;
		border-right: 1px dashed #C4C9D4;
		background: #F5F6FA;
	}
	.s-lane.hide {
		display: none;
	}
	.s-lane:last-child {
		border-right: none;
	}
	/* Left lane (compose + lapidary) uses the same darker tint as Query's
	   .q-input-lane to differentiate the input-side from the stream-side. */
	.s-compose-lane {
		background: #ECEEF3;
	}

	/* Lane header — identical spec to Query.svelte's .q-lane-header so the
	   visual rhythm between /query and /source is the same. */
	.s-lane-header {
		height: 40px;
		box-sizing: border-box;
		display: flex;
		align-items: center;
		padding: 0 16px;
		font-size: 10px;
		font-weight: 600;
		color: #4B5563;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		border-bottom: 1px solid #D8DBE2;
		flex-shrink: 0;
	}
	/* Second header within the Compose lane (Lapidary) — adds a top border
	   so the lane visually breaks into two sections with the same header
	   treatment top + bottom. */
	.s-lapidary-header {
		border-top: 1px solid #D8DBE2;
	}
	.s-lane-meta {
		margin-left: auto;
		font-size: 10px;
		font-weight: 400;
		color: #9CA3AF;
		letter-spacing: 0;
		text-transform: none;
	}

	/* ── Compose section ──────────────────────────────────────────────────── */
	.s-compose-section {
		padding: 16px;
	}
	/* Composite compose box — outer wrapper matches Query's .q-textarea spec
	   (bg / border / radius / focus). Inner textarea + notes input are
	   transparent and share the outer border via the composite container. */
	.s-compose-box {
		background: #F5F6FA;
		border: 1px solid #C4C9D4;
		border-radius: 6px;
		transition: border-color 120ms;
		overflow: hidden;
	}
	.s-compose-box:focus-within {
		border-color: #9CA3AF;
	}

	.s-compose-input {
		display: block;
		width: 100%;
		min-height: 120px;
		padding: 10px 12px;
		background: transparent;
		border: none;
		font-family: inherit;
		font-size: 13px;
		line-height: 1.55;
		color: #1A1A1A;
		resize: vertical;
		outline: none;
		box-sizing: border-box;
	}
	.s-compose-input::placeholder { color: #4B5563; }

	.s-compose-notes {
		display: block;
		width: 100%;
		padding: 8px 12px;
		background: transparent;
		border: none;
		border-top: 1px dotted #C4C9D4;
		font-family: inherit;
		font-size: 12px;
		line-height: 1.5;
		color: #1A1A1A;
		outline: none;
		box-sizing: border-box;
	}
	.s-compose-notes::placeholder {
		color: #9CA3AF;
		font-style: italic;
	}

	.s-compose-actions {
		display: flex;
		justify-content: flex-end;
		align-items: center;
		margin-top: 10px;
		gap: 8px;
	}

	/* Add button — identical spec to Query's .q-submit. */
	.s-plant {
		align-self: flex-end;
		font-size: 12px;
		font-weight: 600;
		color: #111827;
		background: #FFFFFF;
		border: 1px solid #C4C9D4;
		border-radius: 5px;
		padding: 5px 18px;
		cursor: pointer;
		transition: all 120ms ease-out;
	}
	.s-plant:hover:not(:disabled) {
		background: #F5F6FA;
		border-color: #9CA3AF;
	}
	.s-plant:disabled {
		opacity: 0.4;
		cursor: default;
	}

	.s-error {
		margin-top: 8px;
		padding: 8px 10px;
		background: #FEF2F2;
		border-left: 2px solid #DC2626;
		font-size: 12px;
		color: #991B1B;
	}

	/* Pro-tip — identical spec to Query's .q-protip / .q-protip-body / .q-protip-email. */
	.s-protip {
		padding: 20px 16px;
	}
	.s-protip-body {
		margin: 0;
		font-size: 12px;
		line-height: 1.6;
		color: #4B5563;
	}
	.s-protip-email {
		color: #2563EB;
		font-style: normal;
		text-decoration: none;
		user-select: all;
	}
	.s-protip-email:hover {
		text-decoration: underline;
	}

	/* ── Lapidary section ─────────────────────────────────────────────────── */
	.s-lapidary-section {
		padding: 16px;
	}
	.s-lap-row {
		display: flex;
		justify-content: space-between;
		padding: 4px 0;
		font-size: 12px;
		border-bottom: 1px dotted #E1E4EB;
	}
	.s-lap-key { color: #6B7280; }
	.s-lap-val { color: #1F2937; font-variant-numeric: tabular-nums; }
	.s-lap-running .s-lap-val { color: #1F3A68; font-weight: 500; }
	.s-lap-empty {
		font-size: 12px;
		color: #9CA3AF;
		padding: 8px 0;
	}
	.s-submit {
		margin-top: 12px;
		width: 100%;
		background: #FFFFFF;
		border: 1px solid #1F3A68;
		color: #1F3A68;
		padding: 8px 12px;
		font-size: 12px;
		border-radius: 4px;
		cursor: pointer;
		transition: all 150ms ease-out;
	}
	.s-submit:hover:not(:disabled) {
		background: #1F3A68;
		color: #FFFFFF;
	}
	.s-submit:disabled { opacity: 0.5; cursor: not-allowed; }
	.s-submit-msg {
		margin-top: 6px;
		font-size: 11px;
		color: #6B7280;
		text-align: center;
	}

	/* ── Stream ──────────────────────────────────────────────────────────── */
	.s-stream-scroll {
		flex: 1;
		overflow-y: auto;
		padding: 0 16px;
	}

	/* Footer bar — mirrors .s-lane-header on the bottom of the stream lane.
	   Slot for pagination + edit/delete controls. Buttons are placeholder
	   until the backend exposes routes for them. */
	.s-lane-footer {
		height: 40px;
		box-sizing: border-box;
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 0 12px;
		border-top: 1px solid #D8DBE2;
		flex-shrink: 0;
		gap: 8px;
	}
	.s-footer-group {
		display: flex;
		align-items: center;
		gap: 6px;
	}
	.s-footer-btn {
		font-size: 11px;
		font-weight: 500;
		color: #4B5563;
		background: #FFFFFF;
		border: 1px solid #C4C9D4;
		border-radius: 4px;
		padding: 3px 10px;
		cursor: pointer;
		transition: all 120ms ease-out;
	}
	.s-footer-btn:hover:not(:disabled) {
		background: #F5F6FA;
		border-color: #9CA3AF;
	}
	.s-footer-btn:disabled {
		opacity: 0.45;
		cursor: default;
	}
	.s-footer-btn-danger {
		color: #991B1B;
	}
	.s-footer-btn-danger:hover:not(:disabled) {
		border-color: #DC2626;
	}
	.s-footer-page {
		font-size: 11px;
		font-variant-numeric: tabular-nums;
		color: #4B5563;
		padding: 0 4px;
		min-width: 32px;
		text-align: center;
	}
	.s-stream-empty {
		padding: 32px 0;
		font-size: 13px;
		color: #9CA3AF;
		text-align: center;
	}
	.s-empty-hint {
		font-size: 12px;
		color: #C4C9D4;
		margin-top: 6px;
	}

	.s-row {
		border-bottom: 1px solid #E1E4EB;
	}
	.s-row-button {
		width: 100%;
		text-align: left;
		background: none;
		border: none;
		padding: 12px 0;
		cursor: pointer;
		display: flex;
		flex-direction: column;
		gap: 4px;
		transition: background 120ms ease-out;
	}
	.s-row-button:hover { background: #F1F3F7; }

	.s-row-title {
		font-size: 14px;
		line-height: 1.45;
		font-weight: 500;
	}
	.s-row-meta {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-top: 4px;
		font-size: 11px;
		color: #6B7280;
		font-variant-numeric: tabular-nums;
	}
	.s-row-time {
		letter-spacing: 0.02em;
	}
	.s-row-tag {
		padding: 1px 6px;
		border: 1px solid #C4C9D4;
		border-radius: 2px;
		font-size: 9px;
		font-weight: 500;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: #4B5563;
		background: #F1F3F7;
	}
	.s-row-tag-origin {
		color: #4B5563;
	}

	/* ── Lifecycle color states (applied to the title only) ───────────── */
	.row-virgin .s-row-title { color: #3B5582; }    /* slate-ink, lighter dark pen blue */
	.row-clustered .s-row-title { color: #1F3A68; } /* dark pen blue */
	.row-concepted .s-row-title { color: #1F2937; } /* charcoal */
	.row-paused .s-row-title { color: #9CA3AF; font-style: italic; }

	/* ── Expanded detail ────────────────────────────────────────────────── */
	.s-detail {
		padding: 8px 0 16px;
		border-top: 1px dashed #E1E4EB;
	}
	.s-detail-body {
		font-size: 13px;
		line-height: 1.6;
		color: #1F2937;
		white-space: pre-wrap;
		padding: 10px 0;
	}
	.s-detail-notes {
		margin-top: 4px;
		padding: 8px 0;
		font-size: 12px;
	}
	.s-detail-key {
		color: #6B7280;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		font-size: 10px;
		margin-right: 8px;
	}
	.s-detail-val { color: #1F2937; }
	.s-detail-grid {
		margin-top: 10px;
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
		gap: 6px;
	}
	.s-detail-cell {
		font-size: 11px;
		padding: 4px 0;
	}
	.s-detail-link {
		color: #1F3A68;
		font-size: 11px;
		text-decoration: none;
	}
	.s-detail-link:hover { text-decoration: underline; }
	.s-detail-loading {
		font-size: 12px;
		color: #9CA3AF;
		padding: 10px 0;
	}
	.s-detail-actions {
		margin-top: 12px;
		display: flex;
		justify-content: flex-end;
	}
	.s-detail-prune {
		background: none;
		border: 1px solid #D8DBE2;
		color: #6B7280;
		padding: 4px 12px;
		font-size: 11px;
		border-radius: 3px;
		cursor: pointer;
		transition: all 150ms ease-out;
	}
	.s-detail-prune:hover {
		border-color: #DC2626;
		color: #DC2626;
	}

	/* ── Mobile ─────────────────────────────────────────────────────────── */
	@media (max-width: 768px) {
		.s-tab-strip { display: flex; }
		.s-columns {
			grid-template-columns: 1fr;
		}
		.s-lane {
			border-right: none;
		}
	}
</style>
